import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronRight, FileText, Mail, Ticket, Calendar, CreditCard } from 'lucide-react';
import { SyncRun, SyncRecord, SyncSource, CHANGE_BADGE, STATUS_DOT, STATUS_LABEL, SOURCES, relativeTime, formatDuration } from './types';
const SOURCE_ICONS: Record<SyncSource, React.FC<{
  size?: number;
  className?: string;
}>> = {
  confluence: FileText,
  gmail: Mail,
  jira: Ticket,
  gcal: Calendar,
  quickbooks: CreditCard
};
interface Props {
  run: SyncRun;
  onBack: () => void;
  onSelectRecord: (record: SyncRecord) => void;
}
function formatAbsolute(iso: string): string {
  const d = new Date(iso);
  const month = d.toLocaleString('en-US', {
    month: 'short'
  });
  const day = d.getUTCDate();
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${month} ${day} \u00b7 ${hh}:${mm}`;
}
export function SyncRunDetail({
  run,
  onBack,
  onSelectRecord
}: Props) {
  const [filter, setFilter] = useState<'all' | 'new' | 'updated' | 'failed'>('all');
  const SourceIcon = SOURCE_ICONS[run.source];
  const sourceLabel = SOURCES.find(s => s.id === run.source)?.label ?? run.source;
  const absoluteTime = formatAbsolute(run.startedAt);
  const filteredRecords = filter === 'all' ? run.records : run.records.filter(r => r.changeType === filter);
  return <>
      <button onClick={onBack} className="flex items-center gap-2 text-[#8a8680] hover:text-[#e8e4df] transition-colors mb-8 text-[13px]" data-id="element-2141">
        <ArrowLeft size={14} data-id="element-2142" />
        <span data-id="element-2143">Sync History</span>
      </button>

      <div className="bg-[#1c1c22] rounded-[16px] p-6 mb-6 shadow-xl shadow-black/20" data-id="element-2144">
        <div className="flex items-start justify-between gap-4" data-id="element-2145">
          <div className="flex items-center gap-3" data-id="element-2146">
            <div className="w-10 h-10 rounded-[12px] bg-[#252530] flex items-center justify-center shrink-0" data-id="element-2147">
              <SourceIcon size={18} className="text-[#8a8680]" data-id="element-2148" />
            </div>
            <div data-id="element-2149">
              <h1 className="text-[18px] font-medium text-[#e8e4df]" data-id="element-2150">{sourceLabel}</h1>
              <p className="text-[12px] text-[#8a8680] mt-0.5" data-id="element-2151">
                {`run at ${absoluteTime} \u00b7 ${relativeTime(run.startedAt)}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0" data-id="element-2152">
            <span className="text-[12px] text-[#8a8680]" data-id="element-2153">{formatDuration(run.durationMs)}</span>
            <span className="flex items-center gap-2 text-[12px] text-[#8a8680]" data-id="element-2154">
              <span className={`w-2 h-2 rounded-full ${STATUS_DOT[run.status]}`} data-id="element-2155" />
              {STATUS_LABEL[run.status]}
            </span>
          </div>
        </div>

        <div className="h-px bg-[#8a8680]/10 my-4" data-id="element-2156" />

        <div className="flex items-center gap-4 text-[13px]" data-id="element-2157">
          <span className={run.counts.new > 0 ? 'text-[#4a9a9a]' : 'text-[#8a8680]/50'} data-id="element-2158">
            {run.counts.new} new
          </span>
          <span className={run.counts.updated > 0 ? 'text-[#e8a84c]' : 'text-[#8a8680]/50'} data-id="element-2159">
            {run.counts.updated} updated
          </span>
          <span className="text-[#8a8680]/50" data-id="element-2160">{run.counts.unchanged} unchanged</span>
          <span className={run.counts.failed > 0 ? 'text-[#f47067]' : 'text-[#8a8680]/50'} data-id="element-2161">
            {run.counts.failed} failed
          </span>
        </div>

        {run.errorExcerpt && <div className="text-[13px] text-[#f47067] bg-[#f47067]/10 rounded-[10px] px-3 py-2 mt-3 font-mono" data-id="element-2162">
            {run.errorExcerpt}
          </div>}
      </div>

      <div className="flex gap-2 mb-6" data-id="element-2163">
        {(['all', 'new', 'updated', 'failed'] as const).map(f => <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1 rounded-full text-[12px] capitalize transition-colors ${filter === f ? 'bg-[#4a9a9a]/20 text-[#4a9a9a]' : 'bg-white/[0.04] text-[#8a8680] hover:text-[#e8e4df]'}`} data-id="element-2164">
            {f}
          </button>)}
      </div>

      <div className="flex flex-col gap-3" data-id="element-2165">
        {filteredRecords.map((record, i) => {
        const RecordIcon = SOURCE_ICONS[record.source];
        const badge = CHANGE_BADGE[record.changeType];
        return <motion.button key={record.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.25,
          delay: i * 0.03
        }} onClick={() => onSelectRecord(record)} className="bg-[#1c1c22] rounded-[16px] p-5 text-left hover:bg-[#1e1e25] transition-colors w-full shadow-xl shadow-black/20" data-id="element-2166">
              <div className="flex items-center gap-4" data-id="element-2167">
                <div className="w-10 h-10 rounded-[12px] bg-[#252530] flex items-center justify-center shrink-0" data-id="element-2168">
                  <RecordIcon size={18} className="text-[#8a8680]" data-id="element-2169" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-2170">
                  <h3 className="text-[#e8e4df] font-medium text-[15px] truncate" data-id="element-2171">{record.title}</h3>
                  <p className="text-[#8a8680]/60 text-[13px] mt-0.5 truncate" data-id="element-2172">{record.subtitle}</p>
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0" data-id="element-2173">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium ${badge.classes}`} data-id="element-2174">
                    {badge.label}
                  </span>
                  {record.classification && <span className="text-[11px] text-[#8a8680]" data-id="element-2175">{record.classification.label}</span>}
                </div>
                <ChevronRight size={16} className="text-[#8a8680]/40 shrink-0" data-id="element-2176" />
              </div>
            </motion.button>;
      })}
        {filteredRecords.length === 0 && <p className="text-[#8a8680]/50 text-sm text-center py-12" data-id="element-2177">No records in this filter.</p>}
      </div>
    </>;
}